import React, { useState, useRef, useEffect } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import { ProgressBar } from "primereact/progressbar";
import { BaseApiServiceImpl } from "../app_utils/api/BaseApiServiceImpl";
import { UserSessionUtils } from "../app_utils/utils/UserSessionUtils";

const SendEmail: React.FC = () => {
  const [recipients, setRecipients] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [bodyMode, setBodyMode] = useState<"plain" | "html">("html");
  const [isSending, setIsSending] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [walletBalance, setWalletBalance] = useState<number>(0);
  const [pricePerEmail, setPricePerEmail] = useState<number>(1);
  const [emailCredits, setEmailCredits] = useState<number>(0);
  const [requiredEmails, setRequiredEmails] = useState<number>(0);
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);
  const [subscription, setSubscription] = useState<any>(null);
  const toast = useRef<Toast>(null);
  const userId = UserSessionUtils.getUserDetails()?.id;
  const userCountryId = UserSessionUtils.getUserDetails()?.countryId;

  useEffect(() => {
    if (userId) {
      setIsLoadingBalance(true);
      new BaseApiServiceImpl("/api/v1/wallet-deposits/balance-info/" + userId)
        .getRequestWithJsonResponse({})
        .then((response) => {
          const balance = response?.data?.balance ?? 0;
          const pricePerEmailFromApi = response?.data?.pricePerEmail ?? 1;
          setWalletBalance(balance);
          setPricePerEmail(pricePerEmailFromApi);
          setEmailCredits(response?.data?.emailCredits ?? (pricePerEmailFromApi > 0 ? balance / pricePerEmailFromApi : 0));
          setIsLoadingBalance(false);
        })
        .catch(() => {
          setIsLoadingBalance(false);
        });

      new BaseApiServiceImpl("/api/v1/user-email-subscriptions/me")
        .getRequestWithJsonResponse({})
        .then((response) => {
          setSubscription(response?.data);
        })
        .catch(() => {
          setSubscription(null);
        });
    }
  }, [userId]);

  const refreshBalance = () => {
    if (!userId) return;
    setIsLoadingBalance(true);
    new BaseApiServiceImpl("/api/v1/wallet-deposits/balance-info/" + userId)
      .getRequestWithJsonResponse({})
      .then((response) => {
        const balance = response?.data?.balance ?? 0;
        const pricePerEmailFromApi = response?.data?.pricePerEmail ?? 1;
        setWalletBalance(balance);
        setPricePerEmail(pricePerEmailFromApi);
        setEmailCredits(response?.data?.emailCredits ?? (pricePerEmailFromApi > 0 ? balance / pricePerEmailFromApi : 0));
        setIsLoadingBalance(false);
      })
      .catch(() => {
        setIsLoadingBalance(false);
      });
  };

  useEffect(() => {
    const count = parseRecipients().length;
    setRequiredEmails(count);
  }, [recipients]);

  const parseRecipients = (): string[] =>
    recipients
      .split(/[\n,]+/)
      .map((v) => v.trim())
      .filter((v) => v.length > 0);

  const validateForm = (): boolean => {
    const list = parseRecipients();
    if (list.length === 0) {
      toast.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "Enter at least one recipient email (one per line or comma separated).",
      });
      return false;
    }
    if (!subject.trim()) {
      toast.current?.show({ severity: "error", summary: "Validation Error", detail: "Subject is required." });
      return false;
    }
    if (!body.trim()) {
      toast.current?.show({ severity: "error", summary: "Validation Error", detail: "Message body is required." });
      return false;
    }

    if (subscription?.isFreeTier && !subscription?.subscriptionActive) {
      const freeEmailsRemaining = (subscription?.freeTierLimit || 100) - (subscription?.freeEmailsUsed || 0);
      if (list.length > freeEmailsRemaining) {
        toast.current?.show({
          severity: "error",
          summary: "Insufficient Free Tier Emails",
          detail: `You have ${freeEmailsRemaining} free emails remaining. Please upgrade your subscription.`,
        });
        return false;
      }
    } else if (requiredEmails > emailCredits && pricePerEmail > 0) {
      toast.current?.show({
        severity: "error",
        summary: "Insufficient Credits",
        detail: `You need ${requiredEmails} credits but only have ${emailCredits.toFixed(2)}. Please top up your wallet.`,
      });
      return false;
    }

    return true;
  };

  const handleSend = () => {
    setResult(null);
    if (!validateForm()) return;
    setIsSending(true);
    const payload = { to: parseRecipients(), subject, body };
    new BaseApiServiceImpl("/api/v1/messages/email/bulk")
      .postRequestWithJsonResponse(payload)
      .then((response: any) => {
        setResult(response?.data ?? response);
        toast.current?.show({
          severity: response?.success === false ? "warn" : "success",
          summary: "Send result",
          detail: response?.message ?? `Sent ${response?.data?.sent ?? "?"} of ${response?.data?.total ?? "?"} email(s).`,
        });
        refreshBalance();
      })
      .catch((err: any) => {
        const rawMessage = err?.message ?? "Unable to send email";
        const isBrevoAuthError = /Brevo rejected the email \(HTTP 401\)/i.test(rawMessage);
        const detail = isBrevoAuthError
          ? "Brevo rejected the API key or sender. Update the Brevo gateway API key and sender email in Email Gateways."
          : err?.data?.data?.errors?.length
            ? "Per-recipient errors:\n" + err.data.data.errors.join("\n")
            : rawMessage;
        toast.current?.show({ severity: "error", summary: isBrevoAuthError ? "Brevo auth failed" : "Send failed", detail });
        try {
          setResult(typeof err === "object" ? err : { error: String(err) });
        } catch {}
      })
      .finally(() => setIsSending(false));
  };

  const handleUpgrade = () => {
    new BaseApiServiceImpl("/api/v1/user-email-subscriptions/upgrade")
      .postRequestWithJsonResponse({})
      .then((response: any) => {
        setSubscription(response?.data);
        toast.current?.show({
          severity: "success",
          summary: "Subscription Updated",
          detail: "You have been upgraded to a paid subscription.",
        });
      })
      .catch((err: any) => {
        toast.current?.show({ severity: "error", summary: "Upgrade failed", detail: err?.message ?? "Unable to upgrade" });
      });
  };

  const handleRevertToFree = () => {
    new BaseApiServiceImpl("/api/v1/user-email-subscriptions/free-tier")
      .postRequestWithJsonResponse({})
      .then((response: any) => {
        setSubscription(response?.data);
        toast.current?.show({
          severity: "success",
          summary: "Free Tier Activated",
          detail: "You are now on the free tier.",
        });
      })
      .catch((err: any) => {
        toast.current?.show({ severity: "error", summary: "Update failed", detail: err?.message ?? "Unable to update" });
      });
  };

  const isOnFreeTier = subscription?.isFreeTier && !subscription?.subscriptionActive;
  const freeEmailsRemaining = (subscription?.freeTierLimit || 100) - (subscription?.freeEmailsUsed || 0);
  const totalCost = requiredEmails * pricePerEmail;

  return (
    <div className="grid">
      <div className="col-12 md:col-8">
        <div className="card">
          <h3 className="mb-3">Send Email (Broadcast)</h3>
          <p className="text-600 mb-3">
            Send the same message to one or many recipients. Enter recipient email
            addresses, one per line or comma separated.
          </p>

          <div className="field">
            <label htmlFor="recipients">Recipients *</label>
            <InputTextarea
              id="recipients"
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              rows={5}
              placeholder="alice@example.com, bob@example.com&#10;carol@example.com"
              className="w-full"
            />
          </div>

          <div className="field">
            <label htmlFor="subject">Subject *</label>
            <InputText
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject"
              className="w-full"
            />
          </div>

          <div className="field">
            <label htmlFor="body">Message body *</label>
            <div className="mb-2">
              <Button
                label="HTML"
                icon="pi pi-code"
                onClick={() => setBodyMode("html")}
                className={bodyMode === "html" ? "p-button-success mr-2" : "p-button-text mr-2"}
              />
              <Button
                label="Plain text"
                icon="pi pi-font"
                onClick={() => setBodyMode("plain")}
                className={bodyMode === "plain" ? "p-button-success" : "p-button-text"}
              />
            </div>
            <InputTextarea
              id="body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              placeholder={bodyMode === "html" ? "<h1>Hello</h1><p>...</p>" : "Your message here"}
              className="w-full"
              style={{ fontFamily: bodyMode === "html" ? "ui-monospace, monospace" : "inherit" }}
            />
            <small className="text-600">
              {bodyMode === "html"
                ? "HTML is supported by all configured providers (Brevo, SendGrid, Mailgun, SMTP)."
                : "Plain text body."}
            </small>
          </div>

          <Button
            icon="pi pi-send"
            label="Send Email"
            loading={isSending}
            onClick={handleSend}
            className="p-button-primary"
            disabled={requiredEmails <= 0}
          />
        </div>
      </div>

      <div className="col-12 md:col-4">
        {/* Email Cost Estimation */}
        <div className="card" style={{ background: "#f4f6f9" }}>
          <h5 className="mb-3">Email Cost Estimation</h5>
          <div className="mb-2">
            <span className="text-500">Subscription Type:</span>
            <span className="font-bold ml-2">
              {isOnFreeTier ? "Free Tier" : "Paid Subscription"}
            </span>
          </div>

          {isOnFreeTier ? (
            <>
              <div className="mb-2">
                <span className="text-500">Free Emails Remaining:</span>
                <span className="font-bold ml-2">{freeEmailsRemaining}</span>
              </div>
              <div className="mb-2">
                <span className="text-500">Free Emails Used:</span>
                <span className="font-bold ml-2">{subscription?.freeEmailsUsed || 0}</span>
              </div>
              <div className="mb-3">
                <ProgressBar
                  value={((subscription?.freeEmailsUsed || 0) / (subscription?.freeTierLimit || 100)) * 100}
                  style={{ height: "8px" }}
                  className="p-progressbar-success"
                />
              </div>
            </>
          ) : (
            <>
              <div className="mb-2">
                <span className="text-500">Wallet Balance:</span>
                <span className="font-bold ml-2">{isLoadingBalance ? "Loading..." : `${walletBalance.toFixed(2)} UGX`}</span>
              </div>
              <div className="mb-2">
                <span className="text-500">Price per Email:</span>
                <span className="font-bold ml-2">{pricePerEmail.toFixed(2)} UGX</span>
              </div>
              <div className="mb-2">
                <span className="text-500">Available Emails:</span>
                <span className="font-bold ml-2">{emailCredits.toFixed(2)}</span>
              </div>
              <div className="mb-2">
                <span className="text-500">Required Emails:</span>
                <span className="font-bold ml-2">{requiredEmails}</span>
              </div>
              <div className="mb-2">
                <span className="text-500">Total Cost:</span>
                <span className="font-bold ml-2">{totalCost.toFixed(2)} UGX</span>
              </div>
              <div className="mb-3">
                <ProgressBar
                  value={Math.min((emailCredits / Math.max(requiredEmails, 1)) * 100, 100)}
                  style={{ height: "8px" }}
                  className={requiredEmails > emailCredits ? "p-progressbar-danger" : "p-progressbar-success"}
                />
              </div>
              {requiredEmails > emailCredits && (
                <Button
                  label="Top Up Wallet"
                  icon="pi pi-wallet"
                  className="p-button-warning w-full mb-2"
                  onClick={() => {
                    window.location.href = "/wallet";
                  }}
                />
              )}
            </>
          )}

          <div className="mt-3">
            <h5>Subscription Management</h5>
            {isOnFreeTier ? (
              <Button
                label="Upgrade to Paid"
                icon="pi pi-arrow-up"
                className="p-button-success w-full"
                onClick={handleUpgrade}
              />
            ) : (
              <Button
                label="Revert to Free Tier"
                icon="pi pi-arrow-down"
                className="p-button-text w-full"
                onClick={handleRevertToFree}
              />
            )}
          </div>
        </div>

        <div title="Guidance and Manual" className="mt-3">
          <h3>Guidance and Manual</h3>
          <p>
            <strong>Step 1:</strong> Enter recipient email addresses.
          </p>
          <p>
            <strong>Step 2:</strong> Enter the email subject.
          </p>
          <p>
            <strong>Step 3:</strong> Compose your message body (HTML or plain text).
          </p>
          <p>
            <strong>Step 4:</strong> Review the cost estimation on the right.
          </p>
          <p>
            <strong>Step 5:</strong> Click "Send Email" to send.
          </p>
        </div>
      </div>

      <Toast ref={toast} />
    </div>
  );
};

export default SendEmail;
